import React, { FC, ReactNode, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { LinkButton, LoadingSkeletonCard } from 'src/components';
import theme from 'src/utils/theme';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { ROUTES } from 'src/constants/routes.const';
import { randomInteger } from 'src/utils/numbers';
import { usePremiumConfig } from 'src/hooks/usePremiumConfig';
import { PremiumPrice } from 'src/api/internal/schema';
import icons from 'src/components/Icon/icons';
import { useQuery } from '@tanstack/react-query';
import { CurrentStatusResponse } from 'src/api/me/types';
import { MeApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import * as S from './styles';

const { premiumStarNoFill } = icons;

const msgs = defineMessages({
  upgrade: { id: 'scenario_upgrade_to_pro' },
  message1: { id: 'web_ea_home_upsell_unlock_in_tl' },
  message2: { id: 'ea_plans_bullet_v2_2' },
  message3: { id: 'ea_plans_bullet_v2_4' },
  percentOff: { id: 'ea_plans_percent_off' },
  goPro: { id: 'web_ea_home_upsell_pro' },
  lifetimeDiscount: { id: 'lifetime_discount_banner_web' },
  month: { id: 'month' },
  year: { id: 'year' },
});

interface Props {
  price: string;
  originalPrice?: string;
  prefixText?: string;
  text: ReactNode;
  showLoader: boolean;
}

export const UpsellBanner: FC<Props> = ({ price, originalPrice, prefixText, text, showLoader }) => {
  const { safePush } = useSafePush();
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.ms });

  return showLoader ? (
    <S.LoadingWrapper data-testid="loading-wrapper">
      <LoadingSkeletonCard />
    </S.LoadingWrapper>
  ) : (
    <S.Banner
      onClick={
        /* istanbul ignore next: tested manually */
        () => safePush(ROUTES.payment.plans)
      }
      aria-label={intl.formatMessage(msgs.upgrade)}
    >
      <S.ContentWrapper>
        <S.StarWrapper>
          <S.PremiumStar Svg={premiumStarNoFill} />
        </S.StarWrapper>

        <S.TextWrapper>
          {prefixText && <S.Text>{prefixText}</S.Text>}
          <S.Description data-testid="upgrade-banner-text" $desktopOnly={!!prefixText}>
            {text}
          </S.Description>
        </S.TextWrapper>
        <S.PriceWrapper>
          {price}
          {originalPrice !== undefined && <S.OriginalPrice>{originalPrice}</S.OriginalPrice>}
        </S.PriceWrapper>
      </S.ContentWrapper>
      {!isMobile && (
        <S.ButtonWrapper>
          <LinkButton
            buttonType="secondary"
            buttonSize="small"
            href={ROUTES.payment.plans}
            wide={isMobile}
            onClick={
              /* istanbul ignore next: tested manually */
              e => e.stopPropagation()
            }
          >
            {intl.formatMessage(msgs.upgrade)}
          </LinkButton>
        </S.ButtonWrapper>
      )}
    </S.Banner>
  );
};

type UpsellBannerContainerProps = {
  text?: string;
  targetLanguageName?: string;
  isHomeTab?: boolean;
  country?: string;
};

export const InternalUpsellBannerContainer: FC<UpsellBannerContainerProps> = props => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const { text: passedText, targetLanguageName, isHomeTab, country: countryOverride } = props;

  const { data: currentStatusData } = useQuery<CurrentStatusResponse | null>({
    queryKey: ['currentStatus', languagePairId],
    queryFn: () => {
      if (!languagePairId) return null;
      return MeApi.getCurrentStatus({
        languagePairId: languagePairId,
      });
    },
  });
  const { remainingPremiumScenarios } = currentStatusData ?? {};

  const { isLoaded, isError, premiumConfig } = usePremiumConfig({ countryOverride });

  /* istanbul ignore next: tested manually */
  useEffect(() => {
    if (isError) {
      console.error(`Could not display new Upsell banner: failed to load premium config.`);
    }
  }, [isError]);

  if (!isLoaded || !premiumConfig) {
    return null;
  }

  const { prices } = premiumConfig;
  const discountedPrice = premiumConfig.prices.reduce(
    (prevOffer, price) => {
      const prevDiscount = prevOffer?.discount?.percent || 0;
      if (price.discount && price.discount.percent > prevDiscount) {
        return price;
      }

      return prevOffer;
    },
    null as PremiumPrice | null,
  );

  const showLoader =
    (!targetLanguageName || remainingPremiumScenarios === undefined) && !!isHomeTab;
  const formatPrice = (
    { finalPrice, fullPrice, currency, paymentPeriod }: PremiumPrice,
    showFullPrice: boolean = false,
  ) => {
    const priceString = intl.formatNumber(showFullPrice ? fullPrice : finalPrice, {
      style: 'currency',
      currency,
    });

    switch (paymentPeriod) {
      case 'lifetime':
        return priceString;
      case 'month':
        return `${priceString} / ${intl.formatMessage(msgs.month)}`;
      case 'year':
        return `${priceString} / ${intl.formatMessage(msgs.year)}`;
    }
  };
  const messages = [msgs.message1, msgs.message2, msgs.message3];
  const message = messages[randomInteger({ min: 0, max: 2 })];
  const genericText = intl.formatMessage(message, {
    bold: (...chunks: ReactNode[]) => <strong key="strong">{chunks}</strong>,
    count: remainingPremiumScenarios,
    targetLanguage: targetLanguageName,
  });

  if (!discountedPrice) {
    const bannerPrice =
      prices.find(p => p.paymentPeriod === 'year') ||
      prices.find(p => p.paymentPeriod === 'lifetime') ||
      prices[0];

    return (
      <UpsellBanner
        price={formatPrice(bannerPrice)}
        prefixText={intl.formatMessage(msgs.goPro)}
        text={passedText || genericText}
        showLoader={showLoader}
      />
    );
  }

  const specialBannerText =
    discountedPrice.paymentPeriod === 'lifetime'
      ? intl.formatMessage(msgs.lifetimeDiscount, {
          discount: discountedPrice.discount?.percent,
        })
      : null;

  return (
    <UpsellBanner
      price={formatPrice(discountedPrice)}
      originalPrice={formatPrice(discountedPrice, true)}
      prefixText={
        passedText || !specialBannerText
          ? intl.formatMessage(msgs.percentOff, {
              percent: discountedPrice.discount?.percent,
            })
          : ''
      }
      text={passedText || specialBannerText || genericText}
      showLoader={showLoader}
    />
  );
};

export const UpsellBannerContainer: FC<UpsellBannerContainerProps> = props => {
  const user = useUserContext();
  const userIsPro = !!user.user?.isPro;

  if (userIsPro) {
    return null;
  }

  return <InternalUpsellBannerContainer {...props} />;
};
